// Cheese Club custom subscription UI. Writes the selected option's native Seal
// selling-plan id into Horizon's product form, handles the prepaid frequency + gift
// fields, and hides Seal's own widget so the two don't double up.
class CheeseClubSubscription extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  sellingPlanInput = null;

  /** @type {HTMLElement | null} */
  submitText = null;

  /** @type {HTMLTextAreaElement | null} */
  message = null;

  /** @type {HTMLElement | null} */
  messageCount = null;

  /** @type {HTMLElement | null} */
  prefs = null;

  /** @type {HTMLInputElement | null} */
  prefsInput = null;

  /** @type {HTMLElement[]} */
  typeLabels = [];

  /** @type {HTMLElement[]} */
  freqLabels = [];

  /** @type {(HTMLInputElement | HTMLTextAreaElement)[]} */
  giftFields = [];

  /** @type {string} */
  type = 'rolling';

  /** @type {string} */
  prepaidFrequency = 'monthly';

  /** @type {AbortController | null} */
  abortController = null;

  connectedCallback() {
    this.sellingPlanInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="sellingPlanInput"]'));
    this.submitText = /** @type {HTMLElement | null} */ (this.querySelector('[ref="submitText"]'));
    this.message = /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[ref="message"]'));
    this.messageCount = /** @type {HTMLElement | null} */ (this.querySelector('[ref="messageCount"]'));
    this.prefs = /** @type {HTMLElement | null} */ (this.querySelector('[ref="prefs"]'));
    this.prefsInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[ref="prefsInput"]'));

    this.typeLabels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.ccs__type[data-type]')));
    this.freqLabels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.ccs__type[data-freq]')));
    this.giftFields = /** @type {(HTMLInputElement | HTMLTextAreaElement)[]} */ (
      Array.from(this.querySelectorAll('.ccs__gift input, .ccs__gift textarea'))
    );

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const typeRadios = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[name="ccs-type"]')));
    typeRadios.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) this.setType(input.value);
      }, { signal });
    });

    const freqRadios = /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[name="ccs-frequency"]')));
    freqRadios.forEach((input) => {
      input.addEventListener('change', () => {
        if (input.checked) this.setFrequency(input.value);
      }, { signal });
    });

    const optionRadios = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('.ccs__option input[type="radio"]'))
    );
    optionRadios.forEach((input) => {
      input.addEventListener('change', () => {
        this.markActiveOption(input);
        this.sync();
      }, { signal });
    });

    const message = this.message;
    const messageCount = this.messageCount;
    if (message && messageCount) {
      message.addEventListener('input', () => {
        messageCount.textContent = String(message.value.length);
      }, { signal });
    }

    if (this.prefs && this.prefsInput) {
      this.prefs.addEventListener('change', () => this.syncPrefs(), { signal });
    }

    const submit = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="submit"]'));
    if (submit) {
      submit.addEventListener('click', () => this.flyToCart(submit), { signal });
    }

    this.hideSealWidget();

    // Reconcile state with whatever inputs are checked on load: the browser can restore a
    // non-default selection across a refresh, which would otherwise leave this.type and the
    // active styling out of sync with the submitted plan.
    const checkedType = typeRadios.find((input) => input.checked);
    if (checkedType) this.setType(checkedType.value);
    const checkedFreq = freqRadios.find((input) => input.checked);
    if (checkedFreq) this.setFrequency(checkedFreq.value);
    optionRadios.forEach((input) => {
      if (input.checked) this.markActiveOption(input);
    });

    this.syncPrefs();
    this.sync();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  /** @param {string} type */
  setType(type) {
    if (type !== 'rolling' && type !== 'prepaid') return;
    this.type = type;

    this.typeLabels.forEach((label) => {
      label.classList.toggle('ccs__type--active', label.dataset.type === type);
    });

    const panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-panel]')));
    panels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== type;
    });

    // Gift line-item fields only submit values when prepaid is the active type.
    this.giftFields.forEach((field) => {
      field.disabled = type !== 'prepaid';
    });

    this.sync();
  }

  /** @param {string} freq */
  setFrequency(freq) {
    if (freq !== 'monthly' && freq !== 'bimonthly') return;
    this.prepaidFrequency = freq;

    this.freqLabels.forEach((label) => {
      label.classList.toggle('ccs__type--active', label.dataset.freq === freq);
    });

    this.updateOneOffVisibility();
    this.sync();
  }

  // A one-off is a single box, so it has no every-2-months plan: hide it under bimonthly
  // and move the selection on if it was chosen.
  updateOneOffVisibility() {
    const oneOff = /** @type {HTMLElement | null} */ (
      this.querySelector('.ccs__panel--prepaid .ccs__option[data-monthly-only="true"]')
    );
    if (!oneOff) return;
    const hide = this.prepaidFrequency === 'bimonthly';
    oneOff.hidden = hide;

    const oneOffInput = /** @type {HTMLInputElement | null} */ (oneOff.querySelector('input'));
    if (hide && oneOffInput && oneOffInput.checked) {
      const next = /** @type {HTMLInputElement | null} */ (
        this.querySelector('.ccs__panel--prepaid .ccs__option:not([data-monthly-only="true"]) input')
      );
      if (next) {
        next.checked = true;
        this.markActiveOption(next);
      }
    }
  }

  /** @param {HTMLInputElement} input */
  markActiveOption(input) {
    const group = input.closest('.ccs__panel--rolling, .ccs__panel--prepaid');
    if (!group) return;
    group.querySelectorAll('.ccs__option').forEach((option) => {
      option.classList.toggle('ccs__option--active', option.contains(input));
    });
  }

  /** @returns {HTMLElement | null} */
  activeOption() {
    const panel = this.querySelector(`.ccs__panel--${this.type}`);
    if (!panel) return null;
    const checked = panel.querySelector('.ccs__option input[type="radio"]:checked');
    const option = checked ? checked.closest('.ccs__option') : panel.querySelector('.ccs__option');
    return /** @type {HTMLElement | null} */ (option);
  }

  /** @param {HTMLElement} option */
  planIdFor(option) {
    if (option.dataset.plan) return option.dataset.plan;
    if (option.dataset.monthlyOnly === 'true') return option.dataset.planMonthly || '';
    return this.prepaidFrequency === 'bimonthly'
      ? option.dataset.planBimonthly || ''
      : option.dataset.planMonthly || '';
  }

  syncPrefs() {
    if (!this.prefs || !this.prefsInput) return;
    const inputs = /** @type {HTMLInputElement[]} */ (Array.from(this.prefs.querySelectorAll('input:checked')));
    this.prefsInput.value = inputs.map((input) => input.value).join(', ');
  }

  sync() {
    const option = this.activeOption();
    if (!option) return;

    if (this.sellingPlanInput) this.sellingPlanInput.value = this.planIdFor(option);
    this.updateSubmit(option);
  }

  /** @param {HTMLElement} option */
  updateSubmit(option) {
    if (!this.submitText) return;
    const amount = option.querySelector('.ccs__option-amount');
    const amountText = amount?.textContent?.trim() ?? '';
    const period = option.dataset.submitPeriod ? ` ${option.dataset.submitPeriod}` : '';
    const verb = this.type === 'prepaid' ? this.dataset.verbPrepaid : this.dataset.verbRolling;
    this.submitText.textContent = `${verb} · ${amountText}${period}`;
  }

  // This custom form submits directly, bypassing add-to-cart-component's click handler,
  // so Horizon's fly-to-cart never fires. Re-fire it from the visible subscribe button.
  /** @param {HTMLElement} sourceButton */
  flyToCart(sourceButton) {
    const form = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    if (form instanceof HTMLFormElement && !form.checkValidity()) return;

    // Scope to this product's section so a page with other products (recommendations, quick-add) can't hand us the wrong element.
    const productScope = this.closest('.shopify-section') || document;

    const addToCart = productScope.querySelector('add-to-cart-component');
    if (addToCart instanceof HTMLElement && addToCart.dataset.addToCartAnimation !== 'true') return;

    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = productScope
      .querySelector('[data-product-variant-media]')
      ?.getAttribute('data-product-variant-media');
    if (!cartIcon || !image) return;

    const flyToCartElement = /** @type {any} */ (document.createElement('fly-to-cart'));
    flyToCartElement.classList.add('fly-to-cart--main');
    flyToCartElement.style.setProperty('background-image', `url("${image}")`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = sourceButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  hideSealWidget() {
    document.querySelectorAll('.sealsubs-target-element').forEach((widget) => {
      (/** @type {HTMLElement} */ (widget)).style.display = 'none';
    });
  }
}

if (!customElements.get('cheese-club-subscription')) {
  customElements.define('cheese-club-subscription', CheeseClubSubscription);
}
